// DESCRIPTION: Waitlist capture form — shown when an event (or slot) is full
// DESCRIPTION: and the event allows waitlisting. Posts a `waitlisted` booking
// DESCRIPTION: and renders an inline confirmation.

import React, { useState } from 'react';
import { __ } from '@wordpress/i18n';
import apiFetch from '@wordpress/api-fetch';
import { doAction } from '@wordpress/hooks';

/**
 * WaitlistForm — collects name/email (phone optional) and creates a waitlisted
 * booking for a full event.
 *
 * @param {Object}   props
 * @param {number}   props.eventId The event post ID.
 * @param {Object}   props.event   The event object (provides date/time fallbacks).
 * @param {Object}   [props.selectedSlot] Optional selected slot { start, end }.
 * @param {string}   [props.selectedDate] Optional selected ISO date.
 * @param {Function} [props.onSuccess]    Called after a successful join.
 *
 * @return {JSX.Element} The waitlist form or confirmation.
 */
const WaitlistForm = ( {
	eventId,
	event,
	selectedSlot,
	selectedDate,
	onSuccess,
} ) => {
	const [ form, setForm ] = useState( {
		firstName: '',
		lastName: '',
		email: '',
		phone: '',
	} );
	const [ errors, setErrors ] = useState( {} );
	const [ submitting, setSubmitting ] = useState( false );
	const [ joined, setJoined ] = useState( false );

	const update = ( key, value ) => {
		setForm( ( f ) => ( { ...f, [ key ]: value } ) );
		if ( errors[ key ] ) {
			setErrors( ( prev ) => ( { ...prev, [ key ]: '' } ) );
		}
	};

	const validate = () => {
		const e = {};
		if ( ! form.firstName.trim() ) {
			e.firstName = __( 'Please enter your first name', 'nemtly-booking' );
		}
		if ( ! form.lastName.trim() ) {
			e.lastName = __( 'Please enter your last name', 'nemtly-booking' );
		}
		if ( ! form.email.trim() ) {
			e.email = __( 'Please enter your email address', 'nemtly-booking' );
		} else if ( ! /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test( form.email ) ) {
			e.email = __(
				'Please enter a valid email address',
				'nemtly-booking'
			);
		}
		setErrors( e );
		return Object.keys( e ).length === 0;
	};

	const submit = async () => {
		if ( ! validate() || submitting ) {
			return;
		}

		setSubmitting( true );

		const payload = {
			waitlist: true,
			event_id: eventId,
			attendee_name: `${ form.firstName } ${ form.lastName }`.trim(),
			attendee_email: form.email,
			attendee_phone: form.phone || '',
			booking_date: selectedDate || event?.date || '',
			booking_start: selectedSlot?.start || '',
			booking_end: selectedSlot?.end || '',
		};

		/**
		 * Fires before the waitlist join request is sent.
		 *
		 * @param {Object} payload The waitlist payload.
		 */
		doAction( 'nemtly_booking.waitlist.before_submit', payload );

		try {
			const response = await apiFetch( {
				path: '/nemtly-booking/v1/bookings',
				method: 'POST',
				data: payload,
			} );

			if ( ! response.success ) {
				throw new Error(
					response.message ||
						__( 'Could not join the waitlist.', 'nemtly-booking' )
				);
			}

			setJoined( true );
			if ( onSuccess ) {
				onSuccess( response.data );
			}
		} catch ( err ) {
			setErrors( {
				submit:
					err.message ||
					__( 'Could not join the waitlist.', 'nemtly-booking' ),
			} );
		} finally {
			setSubmitting( false );
		}
	};

	if ( joined ) {
		return (
			<div className="nb-app__error">
				<p>
					{ __(
						"You're on the waitlist!",
						'nemtly-booking'
					) }
				</p>
				<p className="nb-waitlist-note">
					{ __(
						'We will email you if a spot becomes available.',
						'nemtly-booking'
					) }
				</p>
			</div>
		);
	}

	return (
		<div className="nb-waitlist">
			<p className="nb-waitlist-heading">
				{ event?.allow_waitlist
					? __(
							'This event is full. Join the waitlist and we will email you if a spot opens up.',
							'nemtly-booking'
					  )
					: __(
							'This event is currently full.',
							'nemtly-booking'
					  ) }
			</p>

			{ event?.allow_waitlist && (
				<>
					<div className="nb-field-grid">
						<div className="nb-field-row">
							<div className="nb-field">
								<label className="nb-label">
									{ __( 'First name', 'nemtly-booking' ) } *
								</label>
								<input
									className={ `nb-input${
										errors.firstName ? ' nb-input--err' : ''
									}` }
									value={ form.firstName }
									onChange={ ( e ) =>
										update( 'firstName', e.target.value )
									}
								/>
								{ errors.firstName && (
									<span className="nb-err">
										{ errors.firstName }
									</span>
								) }
							</div>
							<div className="nb-field">
								<label className="nb-label">
									{ __( 'Last name', 'nemtly-booking' ) } *
								</label>
								<input
									className={ `nb-input${
										errors.lastName ? ' nb-input--err' : ''
									}` }
									value={ form.lastName }
									onChange={ ( e ) =>
										update( 'lastName', e.target.value )
									}
								/>
								{ errors.lastName && (
									<span className="nb-err">
										{ errors.lastName }
									</span>
								) }
							</div>
						</div>
						<div className="nb-field">
							<label className="nb-label">
								{ __( 'Email', 'nemtly-booking' ) } *
							</label>
							<input
								type="email"
								className={ `nb-input${
									errors.email ? ' nb-input--err' : ''
								}` }
								value={ form.email }
								onChange={ ( e ) =>
									update( 'email', e.target.value )
								}
							/>
							{ errors.email && (
								<span className="nb-err">{ errors.email }</span>
							) }
						</div>
					</div>

					{ errors.submit && (
						<div className="nb-err" role="alert">
							{ errors.submit }
						</div>
					) }

					<button
						type="button"
						className="nemtly-button-primary"
						onClick={ submit }
						disabled={ submitting }
					>
						{ submitting
							? __( 'Joining…', 'nemtly-booking' )
							: __( 'Join waitlist', 'nemtly-booking' ) }
					</button>
				</>
			) }
		</div>
	);
};

export default WaitlistForm;
